import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import Checkbox from '@/common/components/Checkbox'
import GoBackButton from '@/common/components/GoBackButton'
import MaskedTextField from '@/common/components/MaskedTextField'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import TextField from '@/common/components/TextField'
import useDebounce from '@/common/hooks/useDebounce'
import { PermissionData } from '@/common/types/permissions'
import { FormTransformers } from '@/common/utils/formTransformers'
import useCreateRole from '@/features/tcardRoles/hooks/useCreateRole'
import { usePermissions } from '@/features/tcardRoles/hooks/usePermissions'
import { roleSchema, RoleValidationSchema } from '@/features/tcardRoles/schemas/create.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { BadgeHelpIcon, ChevronsRightIcon, SearchIcon } from 'lucide-react'
import { useRef, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { Tooltip } from 'react-tooltip'

const Create = () => {
  const navigate = useNavigate()

  const containerRef = useRef<HTMLDivElement>(null)

  const [search, setSearch] = useDebounce('')

  const [selectedPermissions, setSelectedPermissions] = useState<{ permission_id: string }[]>([])
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const {
    data: permissions,
    meta,
    isFetching: permissionsIsFetching,
    fetchNextPage,
  } = usePermissions<PermissionData[]>({
    filters: { search },
  })

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<RoleValidationSchema>({
    resolver: zodResolver(roleSchema),
  })

  const { mutate: createRole, isPending: createRoleIsPending } = useCreateRole({
    permissions: selectedPermissions,
    onSuccess: () => {
      navigate('/tcard-roles')
    },
    onError: FormTransformers.errorsTransformer<RoleValidationSchema>({ setError, setMessages }),
  })

  const onSubmit: SubmitHandler<RoleValidationSchema> = (data) => createRole(data)

  const isToFetchNextPage = () => {
    if (!containerRef.current || meta.current_page >= meta.total_pages || permissionsIsFetching) {
      return false
    }
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current
    return scrollTop + clientHeight >= scrollHeight - 16
  }

  const handleScroll = () => {
    if (isToFetchNextPage()) {
      fetchNextPage()
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-2 items-center">
          <GoBackButton fallback="/tcard-roles" searchLastRouteFromFallback />
          <p className="text-white-400 font-normal">Cargos e permissões - Tcard</p>
          <ChevronsRightIcon color="#ffffffb3" />
          <h2 className="font-bold">Novo cargo</h2>
        </div>

        <Button
          name="submit"
          type="submit"
          className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
          disabled={createRoleIsPending}
          isLoading={createRoleIsPending}
        >
          Confirmar
        </Button>
      </div>

      <Box className="flex flex-col gap-y-6 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] max-h-[calc(100vh-128px)] overflow-y-auto">
        <div className="flex flex-col gap-y-4">
          <p className="text-lg font-bold">Cargo</p>

          <TextField
            label="Nome do cargo"
            markAsRequired
            maxLength={64}
            errorMessage={errors.display_name?.message || undefined}
            {...register('display_name')}
            disabled={createRoleIsPending}
          />

          <TextField
            label="Descrição do cargo"
            markAsRequired
            maxLength={256}
            errorMessage={errors.description?.message || undefined}
            {...register('description')}
            disabled={createRoleIsPending}
          />

          <MaskedTextField
            mask={'00'}
            label='Nível'
            placeholder='0'
            errorMessage={errors.level?.message || undefined}
            {...register('level')}
            disabled={createRoleIsPending}
          />

          <Checkbox
            label="Permitir visualizar dados de empresas que não está vinculado como Responsável Interno ou Substituto do Responsável Interno"
            className="items-center gap-x-2"
            labelClassName="text-[0.625rem] font-light italic"
            errorMessage={errors.can_bypass_linked_client_company_restriction?.message || undefined}
            {...register('can_bypass_linked_client_company_restriction')}
            disabled
          />

          <Checkbox
            label="Permitir visualizar dados de estabelecimentos que não está vinculado como Responsável Interno ou Substituto do Responsável Interno"
            className="items-center gap-x-2"
            labelClassName="text-[0.625rem] font-light italic"
            errorMessage={errors.can_bypass_linked_establishment_company_restriction?.message || undefined}
            {...register('can_bypass_linked_establishment_company_restriction')}
            disabled
          />
        </div>

        <Box className="flex flex-col gap-y-6">
          <div className="flex flex-col gap-y-4">
            <p>Permissões</p>

            <TextField
              LeftIcon={<SearchIcon />}
              name="search"
              placeholder="Pesquisar permissões"
              onChange={(e) => setSearch((e.target as HTMLInputElement).value)}
              disabled={createRoleIsPending}
            />
          </div>

          <div
            ref={containerRef}
            onScroll={handleScroll}
            className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-2 overflow-auto min-h-14 max-h-[calc(100vh-566px)]"
            data-testid="permission_create_list"
          >
            {permissions?.map((permission: any) => (
              <div key={permission.id} className="flex gap-2 items-center">
                <Checkbox
                  name={permission.display_name}
                  label={permission.display_name}
                  labelClassName="max-w-[360px] text-wrap break-words"
                  checked={selectedPermissions.find((value) => value.permission_id === permission.id) ? true : false}
                  onChange={(e) => {
                    if ((e.target as HTMLInputElement).checked) {
                      setSelectedPermissions([...selectedPermissions, { permission_id: permission.id }])
                    } else {
                      setSelectedPermissions(
                        selectedPermissions.filter((value) => value.permission_id !== permission.id),
                      )
                    }
                  }}
                  disabled={createRoleIsPending}
                />

                <div data-tooltip-id={permission.id} className="flex gap-1 items-center text-warning">
                  <BadgeHelpIcon strokeWidth={1} size={14} />

                  <p className="font-light text-[8px] italic">Informações</p>
                </div>

                <Tooltip id={permission.id}>{permission.description}</Tooltip>
              </div>
            ))}
          </div>
        </Box>
      </Box>

      {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
    </form>
  )
}

export default Create
